import React from "react";
import { Box, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { formatDelta, formatPercent, getBudgetHealth } from "../../utils/utilisation";
import UtilisationBar from "./UtilisationBar";
import { Notice, SectionLabel, StatusChip, UtilisationStat } from "./UtilisationParts";
import { toneColors, useUtilisationColors } from "./useUtilisationColors";

const OVER_BUDGET_WARNING =
  "This project has exceeded its budget. Please check with your PM before logging more hours.";

const ViewBudgetLink = ({ onClick, compact }) => {
  const colors = useUtilisationColors();
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: compact ? "flex-start" : "center",
        gap: "6px",
        minHeight: compact ? 32 : 44,
        p: 0,
        border: 0,
        background: "none",
        cursor: "pointer",
        fontFamily: "inherit",
        fontSize: compact ? 13 : 15,
        fontWeight: 700,
        color: colors.tealDeep,
        "&:hover": { textDecoration: "underline" },
      }}
    >
      View budget details
      <ArrowForwardIcon sx={{ fontSize: compact ? 14 : 16 }} />
    </Box>
  );
};

/**
 * The numbers behind a saved time log for ONE fixed-cost project: the person's
 * share of the budget, how the project is doing, and whether the incentive
 * target is still reachable. Employees only ever see percentages and hours.
 *
 * @param {object}  props
 * @param {string}  props.hoursLabel     - hours the user has on the project, e.g. "50h"
 * @param {number}  props.individualPct  - (hours x rate) / budget
 * @param {number}  props.projectPct     - total cost / budget
 * @param {number}  props.targetPercent  - the project's Target % (default 70)
 * @param {number}  props.targetUsedPct  - total cost / (budget x Target %)
 * @param {"full"|"compact"} props.variant - "compact" is the per-project card in a multi-project popup
 * @param {Function} [props.onViewBudget]
 */
const ProjectUtilisationSummary = ({
  hoursLabel,
  individualPct,
  projectPct,
  targetPercent,
  targetUsedPct,
  variant = "full",
  onViewBudget,
}) => {
  const colors = useUtilisationColors();
  const compact = variant === "compact";
  const health = getBudgetHealth({ projectPct, targetUsedPct });

  const budgetTone = health.isOverBudget ? "over" : "ok";
  const targetTone = health.isTargetAchieved ? "ok" : "over";
  const budgetColor = toneColors(colors, budgetTone).main;
  const targetColor = toneColors(colors, targetTone).main;

  const incentiveMessage = health.isTargetAchieved
    ? compact
      ? `If completed within the ${targetPercent}% target, you are eligible for incentive`
      : `If the project is completed within the ${targetPercent}% target, you are eligible for incentive`
    : "Target exceeded — not eligible for incentive";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: compact ? 1.1 : 1.5 }}>
      {compact ? (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
          <UtilisationStat compact outlined={false} label="Your utilisation" value={formatPercent(individualPct)} />
          <UtilisationStat
            compact
            outlined={false}
            label="Project budget used"
            value={formatPercent(projectPct)}
            tone={health.isOverBudget ? "over" : undefined}
          />
        </Box>
      ) : (
        <>
          <SectionLabel>Your contribution</SectionLabel>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.25 }}>
            <UtilisationStat label="Hours by you" value={hoursLabel} />
            <UtilisationStat label="Your utilisation" value={formatPercent(individualPct)} caption="of project budget" />
          </Box>

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.5 }}>
            <SectionLabel>Project health</SectionLabel>
            <StatusChip tone={budgetTone}>{health.isOverBudget ? "Budget exceeded" : "Within budget"}</StatusChip>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 600, color: colors.ink }}>
              Project budget used
            </Typography>
            <Typography component="span" sx={{ fontSize: 13, fontWeight: 800, color: budgetColor }}>
              {formatPercent(projectPct)}
            </Typography>
          </Box>
          <UtilisationBar value={projectPct} tone={budgetTone} label="Project budget used" />
          <Typography sx={{ fontSize: 12, color: colors.ink2 }}>
            {health.isOverBudget
              ? `Exceeded by ${formatDelta(health.exceededByPct)}`
              : `${formatDelta(health.budgetLeftPct)} of the budget left`}
          </Typography>
        </>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: compact ? 0.75 : 0.75,
          pt: compact ? 0 : 0.5,
          borderTop: compact ? "none" : `1px dashed ${colors.line2}`,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", pt: compact ? 0 : 1 }}>
          <Typography component="span" sx={{ fontSize: compact ? 12 : 13, fontWeight: 600, color: colors.ink }}>
            Target used{" "}
            <Typography component="span" sx={{ fontSize: "inherit", fontWeight: 500, color: colors.ink2 }}>
              {compact ? `(target ${targetPercent}%)` : `(target: ${targetPercent}% of budget)`}
            </Typography>
          </Typography>
          <Typography component="span" sx={{ fontSize: compact ? 12 : 13, fontWeight: 800, color: targetColor }}>
            {formatPercent(targetUsedPct)}
          </Typography>
        </Box>
        <UtilisationBar value={targetUsedPct} tone={targetTone} label="Target used" height={compact ? 8 : 10} />
        <Notice tone={targetTone} compact={compact} data-testid="incentive-notice">
          {incentiveMessage}
        </Notice>
        {!compact && (
          <Typography sx={{ fontSize: 12, color: colors.ink2 }}>
            {health.isTargetAchieved
              ? `Target achieved · ${formatDelta(health.targetLeftPct)} of target left`
              : `Target missed · exceeded target by ${formatDelta(health.targetExceededByPct)}`}
          </Typography>
        )}
      </Box>

      {!compact && health.isOverBudget && (
        <Notice tone="over" data-testid="over-budget-warning">
          <Box component="span" sx={{ fontWeight: 400 }}>
            {OVER_BUDGET_WARNING}
          </Box>
        </Notice>
      )}

      {onViewBudget && <ViewBudgetLink compact={compact} onClick={onViewBudget} />}
    </Box>
  );
};

export default ProjectUtilisationSummary;
